import { Vec3, eq, add, mul, sub, EPS, dot, dist, normalize, len, cross, closestPointOnSegment } from './vec';
import { FaceData, Model, faceUsesEdge } from './model';
import { faceBasis, faceNormal, fromBasis2D, offsetLoop2D, pointInPolygon, toBasis2D } from './face';

/**
 * Modellierungs-Operationen wie in SketchUp:
 * geschlossene Formen anlegen, Flächen aufteilen (Form auf Fläche zeichnen),
 * Push/Pull (Extrudieren), Versatz (Offset), Verschieben, Radieren.
 */

export interface ShapeResult {
  vertexIds: number[];
  edgeIds: number[];
  faceId: number | null;
}

/** Findet einen Vertex an (fast) derselben Position oder legt ihn neu an. */
export function findOrCreateVertex(model: Model, p: Vec3): number {
  // Nur mit Geometrie des aktuellen Kontexts verschmelzen — Gruppen bleiben isoliert
  const ctx = model.contextVertexIds();
  for (const v of model.vertices.values()) {
    if (ctx.has(v.id) && eq(v.p, p, 1e-4)) return v.id;
  }
  return model.addVertex(p).id;
}

/**
 * Legt eine geschlossene Form (Kantenring + Fläche) an.
 * Doppelte Punkte werden entfernt; bei selbstberührenden Ringen entfällt nur die Fläche.
 */
export function createClosedShape(model: Model, ptsIn: Vec3[]): ShapeResult {
  const pts: Vec3[] = [];
  for (const p of ptsIn) {
    if (pts.length === 0 || !eq(pts[pts.length - 1]!, p, 1e-5)) pts.push(p);
  }
  if (pts.length > 1 && eq(pts[0]!, pts[pts.length - 1]!, 1e-5)) pts.pop();
  const vertexIds: number[] = [];
  const edgeIds: number[] = [];
  for (const p of pts) vertexIds.push(findOrCreateVertex(model, p));
  const unique = new Set(vertexIds);
  for (let i = 0; i < vertexIds.length; i++) {
    const e = model.addEdge(vertexIds[i]!, vertexIds[(i + 1) % vertexIds.length]!);
    if (e && !edgeIds.includes(e.id)) edgeIds.push(e.id);
  }
  let faceId: number | null = null;
  if (unique.size === vertexIds.length && vertexIds.length >= 3) {
    const f = model.addFace(vertexIds);
    faceId = f ? f.id : null;
  }
  return { vertexIds, edgeIds, faceId };
}

/** Ein einzelner Linienzug (offene Kette) — erzeugt nur Kanten. */
export function createPolyline(model: Model, pts: Vec3[]): ShapeResult {
  const vertexIds: number[] = [];
  const edgeIds: number[] = [];
  for (const p of pts) vertexIds.push(findOrCreateVertex(model, p));
  for (let i = 0; i + 1 < vertexIds.length; i++) {
    const e = model.addEdge(vertexIds[i]!, vertexIds[i + 1]!);
    if (e) edgeIds.push(e.id);
  }
  return { vertexIds, edgeIds, faceId: null };
}

/**
 * Teilt eine Fläche mit einem in ihr liegenden geschlossenen Ring:
 * Die Form wird eigene Fläche (Push/Pull-fähig), die Restfläche erhält ein Loch.
 * Liegt der Ring nicht vollständig im Inneren (berührt den Rand), werden nur
 * die Kanten erzeugt — MVP-Einschränkung gegenüber SketchUps voller Flächenteilung.
 */
export function splitFaceWithLoop(model: Model, faceId: number, loopPts: Vec3[]): ShapeResult {
  const face = model.faces.get(faceId);
  if (!face) return createClosedShape(model, loopPts);
  const outerPts = face.outer.map((id) => model.vertexPosition(id));
  const basis = faceBasis(outerPts);

  // Alle Ringpunkte müssen in der Flächenebene liegen und im Inneren sein.
  let strictlyInside = true;
  for (const p of loopPts) {
    const d2 = toBasis2D(basis, p);
    if (Math.abs(dot(sub(p, basis.origin), basis.n)) > 1e-4) { strictlyInside = false; break; }
    if (!pointInPolygon(d2.x, d2.y, outerPts.map((q) => toBasis2D(basis, q)))) { strictlyInside = false; break; }
    // nicht direkt auf dem Rand liegen haben
    for (let i = 0; i < outerPts.length; i++) {
      const seg = closestPointOnSegment(p, outerPts[i]!, outerPts[(i + 1) % outerPts.length]!);
      if (seg.distance < 1e-4) { strictlyInside = false; break; }
    }
    if (!strictlyInside) break;
  }

  if (!strictlyInside) {
    // Fallback: Kanten anlegen, keine Teilung
    return createClosedShape(model, loopPts);
  }

  const shape = createClosedShape(model, loopPts);
  if (shape.faceId !== null && shape.vertexIds.length >= 3) {
    face.holes.push([...shape.vertexIds]);
  }
  return shape;
}

/**
 * Push/Pull wie SketchUp: verschiebt die Fläche um `delta` und schließt den Körper.
 * - Freie Fläche (keine Nachbarflächen): die Ausgangsfläche bleibt als Boden stehen →
 *   aus einem Rechteck wird ein Quader mit 6 Flächen.
 * - Fläche eines Körpers: Nachbarflächen, in deren Ebene die Zugrichtung liegt,
 *   werden verlängert statt neue Wände zu erzeugen (Quader wird höher/niedriger).
 * - Übrige Kanten des Rings (z. B. Rand eines Lochs in einer Nachbarfläche)
 *   erhalten neue Wandflächen.
 */
export function extrudeFace(model: Model, faceId: number, delta: Vec3): { wallFaceIds: number[] } | null {
  const face = model.faces.get(faceId);
  if (!face) return null;
  const dLen = Math.hypot(delta.x, delta.y, delta.z);
  if (dLen < EPS) return null;
  const dir = mul(delta, 1 / dLen);
  const loops = [face.outer, ...face.holes];
  const loopVerts = new Set(loops.flat());

  // Nachbarflächen je Ringkante; „verlängerbar“, wenn die Zugrichtung in ihrer Ebene liegt
  const ctxFaces = [...model.faces.values()].filter((f) => f.id !== faceId && f.group === face.group);
  const extendable = new Set<number>();
  const edgeNeighbors = new Map<string, FaceData[]>();
  let hasNeighbor = false;
  for (const loop of loops) {
    for (let i = 0; i < loop.length; i++) {
      const a = loop[i]!, b = loop[(i + 1) % loop.length]!;
      const ns = ctxFaces.filter((f) => faceUsesEdge(f, a, b));
      edgeNeighbors.set(edgeKey(a, b), ns);
      if (ns.length > 0) hasNeighbor = true;
      for (const f of ns) {
        const n = faceNormal(f.outer.map((id) => model.vertexPosition(id)));
        if (Math.abs(dot(n, dir)) < 1e-6) extendable.add(f.id);
      }
    }
  }

  // Untere Kopien der Ringecken (alte Positionen)
  const bottom = new Map<number, number>();
  const bottomOf = (v: number): number => {
    let b = bottom.get(v);
    if (b === undefined) {
      b = model.addVertex(model.vertexPosition(v)).id;
      bottom.set(v, b);
    }
    return b;
  };
  for (const v of loopVerts) bottomOf(v);
  const mapB = (v: number) => (loopVerts.has(v) ? bottomOf(v) : v);

  // Nicht verlängerbare Flächen bleiben an den alten Positionen (→ untere Kopien)
  for (const f of model.faces.values()) {
    if (f.id === faceId || extendable.has(f.id)) continue;
    if (![f.outer, ...f.holes].some((l) => l.some((v) => loopVerts.has(v)))) continue;
    f.outer = f.outer.map(mapB);
    f.holes = f.holes.map((h) => h.map(mapB));
  }
  // Kanten außerhalb des Rings: in Zugrichtung verlaufende wachsen mit, andere bleiben stehen
  const ringEdges = new Set<number>();
  for (const loop of loops) {
    for (let i = 0; i < loop.length; i++) {
      const e = model.findEdgeBetween(loop[i]!, loop[(i + 1) % loop.length]!);
      if (e) ringEdges.add(e.id);
    }
  }
  for (const e of [...model.edges.values()]) {
    if (ringEdges.has(e.id)) continue;
    const aIn = loopVerts.has(e.a), bIn = loopVerts.has(e.b);
    if (!aIn && !bIn) continue;
    const [pa, pb] = model.edgePoints(e);
    const along = len(cross(normalize(sub(pb, pa)), dir)) < 1e-6;
    const belongsToExtended = [...extendable].some((fid) => faceUsesEdge(model.faces.get(fid)!, e.a, e.b));
    if (along && belongsToExtended) continue;
    if (aIn) e.a = bottomOf(e.a);
    if (bIn) e.b = bottomOf(e.b);
  }

  // Ring verschieben
  for (const v of loopVerts) {
    const vx = model.vertices.get(v);
    if (vx) vx.p = add(vx.p, delta);
  }

  // Wände für Ringkanten ohne verlängerte Nachbarfläche
  const wallFaceIds: number[] = [];
  for (const loop of loops) {
    for (let i = 0; i < loop.length; i++) {
      const a = loop[i]!, b = loop[(i + 1) % loop.length]!;
      const ns = edgeNeighbors.get(edgeKey(a, b)) ?? [];
      if (ns.some((f) => extendable.has(f.id))) continue;
      const ba = bottomOf(a), bb = bottomOf(b);
      model.addEdge(ba, bb);
      model.addEdge(ba, a);
      model.addEdge(bb, b);
      const wall = model.addFace([ba, bb, b, a], [], undefined, face.color);
      if (wall) wallFaceIds.push(wall.id);
    }
  }

  // Freie Fläche → Ausgangsfläche bleibt als Boden
  if (!hasNeighbor) {
    const faceN = faceNormal(face.outer.map((id) => model.vertexPosition(id)));
    const backwards = dot(faceN, delta) < 0;
    if (backwards) {
      // Nach hinten gezogen: Deckel und Wände umdrehen, Boden behält die Ausrichtung → Normalen außen
      face.outer = [...face.outer].reverse();
      face.holes = face.holes.map((h) => [...h].reverse());
      for (const id of wallFaceIds) {
        const w = model.faces.get(id);
        if (w) w.outer = [...w.outer].reverse();
      }
      model.addFace(face.outer.map(bottomOf).reverse(), face.holes.map((h) => h.map(bottomOf).reverse()), undefined, face.color);
    } else {
      const outer = [...face.outer].reverse().map(bottomOf);
      const holes = face.holes.map((h) => [...h].reverse().map(bottomOf));
      model.addFace(outer, holes, undefined, face.color);
    }
  }

  for (const b of bottom.values()) model.pruneVertex(b);
  return { wallFaceIds };
}

function edgeKey(a: number, b: number): string {
  return a < b ? `${a}:${b}` : `${b}:${a}`;
}

/** Verschiebt die gegebenen Vertices um `delta`. */
export function moveVertices(model: Model, vertexIds: Iterable<number>, delta: Vec3): void {
  for (const id of vertexIds) {
    const v = model.vertices.get(id);
    if (v) v.p = add(v.p, delta);
  }
}

/** Dupliziert Kanten und Flächen mit eigenen Vertices; liefert die neuen IDs. */
export function cloneEntities(
  model: Model,
  edgeIds: Set<number>,
  faceIds: Set<number>
): { edges: Set<number>; faces: Set<number> } {
  const vertexMap = new Map<number, number>();
  const mapVertex = (id: number): number => {
    let n = vertexMap.get(id);
    if (n === undefined) {
      n = model.addVertex(model.vertexPosition(id)).id;
      vertexMap.set(id, n);
    }
    return n;
  };
  // Kopierte Gruppen (inkl. Verschachtelung) werden eigenständige neue Gruppen
  const groupMap = new Map<number, number>();
  const mapGroup = (g: number | undefined): number | undefined => {
    if (g === undefined || g === model.editContext) return g;
    let n = groupMap.get(g);
    if (n === undefined) {
      const src = model.groups.get(g);
      const parent = src?.parent === model.editContext ? model.editContext : mapGroup(src?.parent);
      const ng = model.addGroup(parent);
      ng.name = src?.name;
      ng.axes = src?.axes;
      n = ng.id;
      groupMap.set(g, n);
    }
    return n;
  };
  const newEdges = new Set<number>();
  const newFaces = new Set<number>();
  // Randkanten kopierter Flächen immer mitkopieren — eine Fläche ohne Kanten ist ungültig
  const allEdges = new Set(edgeIds);
  for (const id of faceIds) {
    const f = model.faces.get(id);
    if (!f) continue;
    for (const loop of [f.outer, ...f.holes]) {
      for (let i = 0; i < loop.length; i++) {
        const a = loop[i]!, b = loop[(i + 1) % loop.length]!;
        for (const e of model.edges.values()) {
          if (e.group === f.group && ((e.a === a && e.b === b) || (e.a === b && e.b === a))) allEdges.add(e.id);
        }
      }
    }
  }
  for (const id of allEdges) {
    const e = model.edges.get(id);
    if (!e) continue;
    const ne = model.addEdge(mapVertex(e.a), mapVertex(e.b));
    if (ne) {
      ne.group = mapGroup(e.group);
      newEdges.add(ne.id);
    }
  }
  for (const id of faceIds) {
    const f = model.faces.get(id);
    if (!f) continue;
    const outer = f.outer.map(mapVertex);
    const holes = f.holes.map((h) => h.map(mapVertex));
    const nf = model.addFace(outer, holes, undefined, f.color);
    if (nf) {
      nf.group = mapGroup(f.group);
      newFaces.add(nf.id);
    }
  }
  return { edges: newEdges, faces: newFaces };
}

/**
 * Spiegeln wie SketchUps „Spiegeln entlang“: Die Auswahl wird an der Ebene durch
 * ihre Mitte gespiegelt, deren Normale `axis` ist. Flächenringe werden umgedreht,
 * damit die Vorderseiten nach dem Spiegeln weiter nach außen zeigen; Achsen
 * enthaltener Gruppen werden mitgespiegelt.
 */
export function flipEntities(model: Model, edgeIds: Iterable<number>, faceIds: Iterable<number>, axis: Vec3): boolean {
  const vids = model.verticesOf(edgeIds, faceIds);
  if (vids.size === 0) return false;
  const n = normalize(axis);
  let min = Infinity, max = -Infinity;
  for (const id of vids) {
    const d = dot(model.vertexPosition(id), n);
    min = Math.min(min, d);
    max = Math.max(max, d);
  }
  const mid2 = min + max;
  for (const id of vids) {
    const v = model.vertices.get(id)!;
    v.p = add(v.p, mul(n, mid2 - 2 * dot(v.p, n)));
  }
  // Alle Flächen, deren Ecken komplett gespiegelt wurden, umorientieren
  for (const f of model.faces.values()) {
    if (!f.outer.every((v) => vids.has(v))) continue;
    f.outer = [...f.outer].reverse();
    f.holes = f.holes.map((h) => [...h].reverse());
  }
  const groups = model.groupsAt(edgeIds, faceIds);
  const mirror = (v: Vec3): Vec3 => sub(v, mul(n, 2 * dot(v, n)));
  model.rotateGroupAxes(groups, mirror);
  return true;
}

/** Wendet eine Abbildung auf die gegebenen Vertices an (Drehen/Skalieren). */
export function transformVertices(model: Model, vertexIds: Iterable<number>, f: (p: Vec3) => Vec3): void {
  for (const id of vertexIds) {
    const v = model.vertices.get(id);
    if (v) v.p = f(v.p);
  }
}

/** Radierer: Kanten löschen (Flächen kaskadieren mit). */
export function eraseEdges(model: Model, edgeIds: Iterable<number>): void {
  for (const id of edgeIds) model.deleteEdge(id);
}

/**
 * Versatz (Offset) einer Fläche nach innen: erzeugt eine innere, parallele Form
 * und teilt die Fläche (wie SketchUps Offset-Werkzeug auf einer Fläche).
 */
export function offsetFace(model: Model, faceId: number, distance: number): { innerFaceId: number | null } | null {
  const face = model.faces.get(faceId);
  if (!face || face.holes.length > 0) return null;
  const outerPts = face.outer.map((id) => model.vertexPosition(id));
  const basis = faceBasis(outerPts);
  const poly2 = outerPts.map((p) => toBasis2D(basis, p));

  // Umlaufrichtung prüfen: offsetLoop2D erwartet CCW → bei CW Distanz negieren
  let area = 0;
  for (let i = 0; i < poly2.length; i++) {
    const a = poly2[i]!, b = poly2[(i + 1) % poly2.length]!;
    area += a.x * b.y - b.x * a.y;
  }
  const d = area >= 0 ? distance : -distance;

  const offset2 = offsetLoop2D(poly2, d);
  const offset3 = offset2.map((p) => fromBasis2D(basis, p.x, p.y));
  // Kollaps-Prüfung: Offsetfläche muss positiv und kleiner als das Original sein
  let areaOrig = 0, areaOff = 0;
  for (let i = 0; i < poly2.length; i++) {
    const a = poly2[i]!, b = poly2[(i + 1) % poly2.length]!;
    areaOrig += a.x * b.y - b.x * a.y;
    const c = offset2[i]!, e = offset2[(i + 1) % offset2.length]!;
    areaOff += c.x * e.y - e.x * c.y;
  }
  if (areaOff <= 1e-9 || areaOff >= areaOrig - 1e-9) return null;

  const shape = splitFaceWithLoop(model, faceId, offset3);
  return { innerFaceId: shape.faceId };
}

function centroid2(poly: { x: number; y: number }[]) {
  let x = 0, y = 0;
  for (const p of poly) { x += p.x; y += p.y; }
  return { x: x / poly.length, y: y / poly.length };
}

/** Entfernt alle freistehenden (nicht von Kanten begrenzten) Vertices — Aufräumen nach Radieren. */
export function cleanupModel(model: Model): void {
  model.pruneOrphanVertices();
}

/** Distanz des Punktes zur Fläche (senkrecht auf deren Ebene, wenn innerhalb, sonst ∞-artig groß). */
export function pointOnFace(p: Vec3, model: Model, faceId: number): boolean {
  const face = model.faces.get(faceId);
  if (!face) return false;
  const outerPts = face.outer.map((id) => model.vertexPosition(id));
  const basis = faceBasis(outerPts);
  if (Math.abs(dot(sub(p, basis.origin), basis.n)) > 1e-4) return false;
  const d2 = toBasis2D(basis, p);
  return pointInPolygon(d2.x, d2.y, outerPts.map((q) => toBasis2D(basis, q)));
}

export function faceById(model: Model, id: number): FaceData | null {
  return model.faces.get(id) ?? null;
}

export function moveFace(model: Model, faceId: number, delta: Vec3): void {
  const f = model.faces.get(faceId);
  if (!f) return;
  const ids = new Set<number>();
  for (const v of f.outer) ids.add(v);
  for (const h of f.holes) for (const v of h) ids.add(v);
  moveVertices(model, ids, delta);
}

/** Hilfsfunktion für Tests/Werkzeuge: Mittelpunkt einer Strecke. */
export function segMidpoint(a: Vec3, b: Vec3): Vec3 {
  return mul(add(a, b), 0.5);
}

/**
 * Setzt die Länge einer Kante: Startpunkt bleibt fix, Endpunkt wandert entlang
 * der Kantenrichtung. Verbundene Geometrie am Endpunkt wandert mit (SketchUp-sticky).
 */
export function setEdgeLength(model: Model, edgeId: number, lengthM: number): boolean {
  const e = model.edges.get(edgeId);
  if (!e || !(lengthM > 1e-6)) return false;
  const a = model.vertexPosition(e.a);
  const b = model.vertexPosition(e.b);
  const dir = normalize(sub(b, a));
  if (len(dir) < 0.5) return false; // entartete Kante
  const v = model.vertices.get(e.b);
  if (!v) return false;
  v.p = add(a, mul(dir, lengthM));
  return true;
}

/**
 * Zusammenhängender Flächenverbund: alle Flächen, die über gemeinsame
 * Kanten (Ringabschnitte) erreichbar sind — erkennt z. B. einen kompletten
 * Push/Pull-Körper von einer einzigen angeklickten Fläche aus.
 */
export function connectedFaceComponent(model: Model, startFaceId: number): Set<number> {
  const edgeMap = new Map<string, number[]>();
  const key = (a: number, b: number): string => (a < b ? `${a}:${b}` : `${b}:${a}`);
  for (const f of model.faces.values()) {
    for (const loop of [f.outer, ...f.holes]) {
      for (let i = 0; i < loop.length; i++) {
        const k = key(loop[i]!, loop[(i + 1) % loop.length]!);
        const arr = edgeMap.get(k);
        if (arr) arr.push(f.id);
        else edgeMap.set(k, [f.id]);
      }
    }
  }
  const out = new Set<number>([startFaceId]);
  const queue: number[] = [startFaceId];
  while (queue.length > 0) {
    const f = model.faces.get(queue.pop()!);
    if (!f) continue;
    for (const loop of [f.outer, ...f.holes]) {
      for (let i = 0; i < loop.length; i++) {
        for (const nid of edgeMap.get(key(loop[i]!, loop[(i + 1) % loop.length]!)) ?? []) {
          if (!out.has(nid)) { out.add(nid); queue.push(nid); }
        }
      }
    }
  }
  return out;
}

/** Skaliert Vertices achsweise um einen Fixpunkt (Faktor 1 = unverändert). */
export function scaleAboutAnchor(
  model: Model,
  vertexIds: Iterable<number>,
  anchor: Vec3,
  fx: number,
  fy: number,
  fz: number
): void {
  transformVertices(model, vertexIds, (p) => ({
    x: anchor.x + (p.x - anchor.x) * fx,
    y: anchor.y + (p.y - anchor.y) * fy,
    z: anchor.z + (p.z - anchor.z) * fz
  }));
}

export function closestOnPolyline(p: Vec3, pts: Vec3[]): Vec3 {
  let best = pts[0]!;
  let bestD = Infinity;
  for (let i = 0; i + 1 < pts.length; i++) {
    const hit = closestPointOnSegment(p, pts[i]!, pts[i + 1]!);
    if (hit.distance < bestD) { bestD = hit.distance; best = hit.point; }
  }
  return best;
}
